import { Box, Button, FormControlLabel, Stack, Link, Typography } from "@mui/material";

import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useEffect, useState } from "react";
import logo from "@/assets/image/logo.png";
import { books } from "@/assets/books";
import { schema } from "./schema";
import { useNavigate } from "react-router-dom";
import { useLoginUserMutation } from "@/store/api/authApi";
import { useLazyGetInactivityQuery } from "@/store/api/notificationApi";
import { setToken } from "@/helpers/cookiesHelper";
import { setAuthData } from "@/store/slice/authSlice";
import { setAwayInfo, setAwayBadge } from "@/store/slice/notificationSlice";
import { playMoneySound } from "@/helpers/moneySound";
import { useAppDispatch } from "@/hooks/useAppDispatch";
import { styles } from "./stylesLogin";
import CustomInput from "@/components/UI/CustomInput/CustomInput";
import CustomCheckbox from "@/components/UI/CustomCheckbox/CustomCheckbox";

interface IFormInput {
  email: string;
  password: string;
  rememberMe?: boolean;
}

const Login = () => {
  const [errorFlag, setErrorFlag] = useState(false);
  const navigate = useNavigate();
  const [showEmailError, setShowEmailError] = useState(false);
  const [showPasswordError, setShowPasswordError] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<IFormInput>({
    resolver: yupResolver(schema),
    mode: "onChange",
  });
  const dispatch = useAppDispatch();
  const emailValue = watch("email");
  const passwordValue = watch("password");

  const [loginUser] = useLoginUserMutation();
  const [fetchInactivity] = useLazyGetInactivityQuery();

  const announceAwayEarnings = async () => {
    try {
      const res = await fetchInactivity().unwrap();
      if (res?.totalEarnedWhileInactive > 0) {
        dispatch(
          setAwayInfo({
            amount: res.totalEarnedWhileInactive,
            seconds: res.totalInactiveSeconds,
          })
        );
        dispatch(setAwayBadge(true));
        playMoneySound();
      }
    } catch (err) {
      console.error("Failed to fetch away earnings:", err);
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      if (emailValue) setShowEmailError(true);
    }, 500);
    return () => clearTimeout(timer);
  }, [emailValue]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (passwordValue) setShowPasswordError(true);
    }, 500);
    return () => clearTimeout(timer);
  }, [passwordValue]);

  const onSubmit = async (data: IFormInput) => {
    const { email, password } = data;
    try {
      const response = await loginUser({ email, password }).unwrap();
      if (response.token) {
        setToken(response.token);
        dispatch(setAuthData({ user: response.user }));
        announceAwayEarnings();
        navigate("/");
      } else {
        console.error("Login failed");
      }
    } catch (err) {
      setErrorFlag(true);
      console.error("Login error:", err);
    }
  };

  useEffect(() => {
    if (emailValue || passwordValue) {
      setErrorFlag(false);
    }
  }, [emailValue, passwordValue]);

  return (
    <Box sx={styles.wrapper}>
      <Box sx={styles.logoWrapper}>
        <Box sx={styles.logoBox}>
          <img
            src={logo}
            alt="Wethemrkt Logo"
            width={102}
            height={32}
            style={{ objectFit: "contain" }}
          />
        </Box>

        <Box sx={styles.titleWrapper}>
          <Typography sx={styles.title}>Login your account</Typography>
          <Typography sx={styles.subtitle}>Enter your email and password below</Typography>
        </Box>
      </Box>

      {errorFlag && (
        <Typography sx={styles.errorText} variant="h5">
          One or more details is incorrect
        </Typography>
      )}

      <Box component="form" onSubmit={handleSubmit(onSubmit)} sx={{ width: "100%" }}>
        <Stack
          sx={{
            display: "flex",
            gap: "32px",
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: "24px",
            }}
          >
            <CustomInput
              label="Email"
              placeholder="Enter your email"
              error={showEmailError && !!errors.email}
              helperText={showEmailError ? errors.email?.message : ""}
              register={register}
              name="email"
            />

            <CustomInput
              label="Password"
              placeholder="Enter your password"
              type="password"
              error={showPasswordError && !!errors.password}
              helperText={showPasswordError ? errors.password?.message : ""}
              register={register}
              name="password"
            />
          </Box>

          <Box
            sx={{
              height: "142",
              display: "flex",
              flexDirection: "column",
              gap: "22px",
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <FormControlLabel
                control={
                  <CustomCheckbox
                    size={24}
                    checked={rememberMe}
                    onChange={(val) => {
                      setRememberMe(val);
                    }}
                  />
                }
                label="Remember me"
                sx={styles.formControlLabel}
              />
              <Link href={books.forgotPassword} sx={styles.Link}>
                Forgot password?
              </Link>
            </Box>

            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={!emailValue || !passwordValue}
              sx={styles.button}
            >
              Log in
            </Button>

            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                gap: "6px",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <Typography sx={styles.noAccountText}>Don&apos;t have an account? </Typography>
              <Link
                href={books.signUp}
                underline="always"
                sx={{
                  ...styles.Link,
                  fontWeight: 600,
                  fontSize: "15px",
                  lineHeight: "22px",
                }}
              >
                Sign up
              </Link>
            </Box>
          </Box>
        </Stack>
      </Box>
    </Box>
  );
};

export default Login;
